import { useTranslations } from 'next-intl';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';

import { PersonIcon } from '@/component/Icons/PersonIcon';
import { CloseIcon } from '@/component/Icons/CloseIcon';
import { DefaultDropdown } from '@/component/Dropdown';
import { GuestsDropdown } from '@/component/dropdowns';
import { useListingSearchStore } from '@/store';
import { useSearchSubmit } from '../../hooks';
import { GuestsModal } from './GuestsModal';

import dropdownStyles from '@/component/dropdowns/GuestsDropdown/GuestsDropdown.module.css';
import styles from './GuestsField.module.css';

export const SearchListingGuestsField = ({ currentStep, onSetStep, theme }) => {
  const {
    guests,
    invalidField,
    setInvalidField,
    focusField,
    setFocusField,
    setGuests,
    togglePets,
    incrementGuest,
    decrementGuest,
    pets,
    onReset,
  } = useListingSearchStore();

  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useMediaQuery({ query: '(max-width: 640px)' });
  const isMobileRef = useRef(isMobile);
  const { onSearch } = useSearchSubmit();

  const handleDropdownOpen = (e) => {
    // HACK: if we clicked to clear button, we don't want to open anything
    if (e.target.tagName.toLowerCase() === 'button') return;
    if (!isMobile) {
      setTimeout(() => {
        document
          .getElementById('search-section')
          .scrollIntoView({ block: 'end' });
      }, 100);
    }
    if (isMobile) {
      onSetStep('guests');
      return;
    }
    setIsVisible(true);
    setFocusField('guests');
  };

  const handleDropdownClose = () => {
    setIsVisible(false);
    if (focusField === 'guests') {
      setFocusField(null);
    }
  };

  const handleCloseModal = (e) => {
    if (e) e.stopPropagation();
    setIsVisible(false);
    onSetStep(null);
  };

  const onSearchListing = () => {
    onSearch();
    onSetStep(null);
  };

  const t = useTranslations();
  const ref = useDetectClickOutside({ onTriggered: handleDropdownClose });

  const totalGuests = Object.keys(guests).reduce(
    (acc, key) => (acc += guests[key]),
    0,
  );

  useEffect(() => {
    if (invalidField === 'guests') {
      setInvalidField(null);
      setIsVisible(true);
    }
  }, [invalidField]);

  const handleClearValue = useCallback(() => {
    setGuests();
    togglePets(false);
  }, []);

  const handleClearAll = (e) => {
    e.stopPropagation();
    onReset();
    onSetStep(null);
  };

  useEffect(() => {
    if (isMobileRef.current !== isMobile && !isMobile) {
      handleCloseModal();
    }
    isMobileRef.current = isMobile;
  }, [isMobile]);

  const handleBlur = (e) => {
    if (
      e.relatedTarget &&
      ref.current &&
      !ref.current.contains(e.relatedTarget)
    ) {
      setFocusField(null);
      setIsVisible(false);
    }
  };

  return (
    <section
      className={`guests-box ${styles.container} ${
        focusField === 'guests' ? '!bg-white !rounded-r-none' : ''
      } ${theme === 'compact' ? styles.compact : ''}
      ${theme === 'vertical' ? styles.vertical : ''}`}
      ref={ref}
      tabIndex={1}
      onFocus={handleDropdownOpen}
      onBlur={handleBlur}
    >
      <div
        className={`${styles.box} search-input-box ${
          focusField === 'guests' ? 'bg-slate-100 !border-slate-300' : ''
        }`}
      >
        <div className={styles.input}>
          <span
            className={`icon-input ${styles.icon} ${
              focusField === 'guests' ? '!text-teal-600' : ''
            }`}
          >
            <PersonIcon />
          </span>

          <div className="flex flex-col gap-2 min-w-0 search-item-box">
            <label className="text-xs text-slate-600 truncate inline-block h-[18px] mt-[-4px] mb-[-7px]">
              <span>{t('guests')}</span>
            </label>
            {!totalGuests && (
              <span
                className={`inline-block font-medium text-slate-400 mt-[-5px] mb-[-7px] truncate search-item-input max-sm:text-sm ${theme === 'vertical' ? 'text-[14px]' : ''}`}
              >
                {t('add-guests')}
              </span>
            )}
            {totalGuests > 0 && (
              <span
                className={`inline-block font-medium text-black mt-[-5px] mb-[-7px] truncate search-item-input ${theme === 'vertical' ? 'text-[14px]' : ''}`}
              >
                {`${totalGuests} ${
                  totalGuests === 1 ? t('guest') : t('guests')
                }`}
              </span>
            )}
          </div>
        </div>

        {(totalGuests > 0 || pets) && (
          <button
            className="bg-transparent self-center"
            type="button"
            onClick={handleClearValue}
            tabIndex={1}
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {isVisible && (
        <DefaultDropdown className={dropdownStyles.dropdown_container}>
          <GuestsDropdown
            guests={guests}
            pets={pets}
            onIncrement={incrementGuest}
            onDecrement={decrementGuest}
            onTogglePets={togglePets}
          />
        </DefaultDropdown>
      )}

      {currentStep === 'guests' && isMobile && (
        <GuestsModal onSearch={onSearchListing} onClearAll={handleClearAll} />
      )}
    </section>
  );
};
